import React from "react";
import ReactMarkdown from "react-markdown";

// Tailwind-styled markdown for agent chat replies (no typography plugin in
// this project, so elements are styled individually).
const COMPONENTS = {
  p: (props) => (
    <p className="text-[16px] leading-relaxed text-gray-800 mb-2.5 last:mb-0" {...props} />
  ),
  strong: (props) => <strong className="font-semibold text-gray-900" {...props} />,
  em: (props) => <em {...props} />,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-ktx-purple hover:underline"
    >
      {children}
    </a>
  ),
  ul: (props) => (
    <ul
      className="list-disc pl-5 mb-2.5 last:mb-0 space-y-1 text-[16px] leading-relaxed text-gray-800"
      {...props}
    />
  ),
  ol: (props) => (
    <ol
      className="list-decimal pl-5 mb-2.5 last:mb-0 space-y-1 text-[16px] leading-relaxed text-gray-800"
      {...props}
    />
  ),
  li: (props) => <li className="text-[16px] leading-relaxed text-gray-800" {...props} />,
  h1: (props) => <p className="text-lg font-bold text-gray-900 mb-2" {...props} />,
  h2: (props) => <p className="text-lg font-bold text-gray-900 mb-2" {...props} />,
  h3: (props) => <p className="text-[16px] font-semibold text-gray-900 mb-1.5" {...props} />,
  h4: (props) => <p className="text-[16px] font-semibold text-gray-900 mb-1" {...props} />,
  code: ({ inline, ...props }) =>
    inline ? (
      <code
        className="rounded bg-gray-100 px-1 py-0.5 text-[14px] font-mono text-gray-800"
        {...props}
      />
    ) : (
      <code className="block text-[14px] font-mono text-gray-800" {...props} />
    ),
  pre: (props) => (
    <pre
      className="rounded-md bg-gray-100 border border-gray-200 px-2.5 py-2 mb-2.5 last:mb-0 overflow-x-auto"
      {...props}
    />
  ),
  blockquote: (props) => (
    <blockquote
      className="border-l-2 border-gray-300 pl-3 text-[16px] leading-relaxed text-gray-600 mb-2.5 last:mb-0"
      {...props}
    />
  ),
  hr: () => <hr className="my-2.5 border-gray-200" />,
  table: (props) => (
    <div className="overflow-x-auto mb-2.5 last:mb-0">
      <table className="text-[15px] border-collapse" {...props} />
    </div>
  ),
  th: (props) => (
    <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold" {...props} />
  ),
  td: (props) => <td className="border border-gray-200 px-2 py-1" {...props} />,
};

// Takes its size, weight and color from the surrounding text, e.g. a DataList value.
const COMPACT_COMPONENTS = {
  ...COMPONENTS,
  p: (props) => <p className="mb-1.5 last:mb-0" {...props} />,
  strong: (props) => <strong className="font-semibold" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 mb-1.5 last:mb-0 space-y-0.5" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 mb-1.5 last:mb-0 space-y-0.5" {...props} />,
  li: (props) => <li {...props} />,
  h1: (props) => <p className="font-semibold mb-1" {...props} />,
  h2: (props) => <p className="font-semibold mb-1" {...props} />,
  h3: (props) => <p className="font-semibold mb-1" {...props} />,
  h4: (props) => <p className="font-semibold mb-1" {...props} />,
  code: ({ inline, ...props }) =>
    inline ? (
      <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.95em] font-mono" {...props} />
    ) : (
      <code className="block text-[0.95em] font-mono" {...props} />
    ),
  blockquote: (props) => (
    <blockquote className="border-l-2 border-gray-300 pl-3 text-gray-600 mb-1.5 last:mb-0" {...props} />
  ),
  table: (props) => (
    <div className="overflow-x-auto mb-1.5 last:mb-0">
      <table className="border-collapse" {...props} />
    </div>
  ),
};

const MarkdownText = ({ text, compact = false }) => (
  <div className="break-words">
    <ReactMarkdown components={compact ? COMPACT_COMPONENTS : COMPONENTS}>{text || ""}</ReactMarkdown>
  </div>
);

export default MarkdownText;
